import React, { useState, useEffect, useCallback, useId } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import ErrorState from './ErrorState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { getInvoiceDefaults, saveInvoiceDefaults } from '../utils/billingStore';

/**
 * <QuoteTermsSettings /> — the terms printed on the last page of every quote.
 *
 * The validity and payment clauses aren't free text: they're generated from the
 * numbers the document itself uses, so the terms page can never contradict the
 * "Valid until" date on the front.
 *
 * Server first: the editor stays locked until the company settings have loaded,
 * so a failed load can never save the built-in defaults over the team's terms.
 */

const box = { border: '1px solid var(--cw-border)', borderRadius: 12, background: 'var(--cw-surface)' };
const KEYS = ['quoteValidDays', 'quoteTerms', 'quoteSectionPresets', 'quoteAcceptance'];
const pick = (o) => Object.fromEntries(KEYS.map((k) => [k, o?.[k]]));

const QuoteTermsSettings = ({ onDirtyChange }) => {
  const uid = useId().replace(/:/g, '');
  const { confirm } = useConfirm();
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const s = await apiFetch('/api/contractor/settings');
      const serverDefaults = s?.data?.invoiceDefaults && typeof s.data.invoiceDefaults === 'object' ? s.data.invoiceDefaults : {};
      // Built-in defaults fill gaps only for keys the company has never saved.
      const next = pick({ ...getInvoiceDefaults(), ...serverDefaults });
      setForm(next);
      setSaved(next);
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = !!form && !!saved && JSON.stringify(form) !== JSON.stringify(saved);
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPreset = (i, patch) => set('quoteSectionPresets', form.quoteSectionPresets.map((p, n) => (n === i ? { ...p, ...patch } : p)));

  const days = Number(form?.quoteValidDays);
  const daysError = form && (!Number.isInteger(days) || days < 1 || days > 365) ? 'Enter a whole number of days from 1 to 365' : null;
  const presetErrors = (form?.quoteSectionPresets || []).map((p) => (!String(p.title || '').trim() ? 'Give this section a heading' : null));
  const invalid = !!daysError || presetErrors.some(Boolean);

  const save = async () => {
    if (invalid) { toast.error('Fix the highlighted fields before saving'); return; }
    setSaving(true);
    try {
      const patch = {
        ...form,
        quoteValidDays: days,
        quoteSectionPresets: (form.quoteSectionPresets || []).map((p) => (p.kind === 'table'
          ? p
          : { ...p, bullets: (p.bullets || []).map((b) => b.trim()).filter(Boolean) })),
      };
      // Only the quote keys are sent; the server deep-merges them into invoiceDefaults.
      await apiFetch('/api/contractor/settings', { method: 'PUT', body: JSON.stringify({ data: { invoiceDefaults: patch } }) });
      saveInvoiceDefaults(patch); // refresh this browser's copy only after the server accepted it
      setForm(patch);
      setSaved(patch);
      toast.success('Quote terms saved');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not save the quote terms'));
    } finally { setSaving(false); }
  };

  const removePreset = async (i) => {
    const title = form.quoteSectionPresets[i]?.title || 'this section';
    const ok = await confirm({
      title: `Remove “${title}”?`,
      body: 'It stops being offered on new quotes once you save. Quotes that already use it keep their wording.',
      confirmLabel: 'Remove preset',
      tone: 'danger',
    });
    if (ok) set('quoteSectionPresets', form.quoteSectionPresets.filter((_, n) => n !== i));
  };

  if (loading && !form) return <p className="cw-meta" role="status">Loading quote terms…</p>;
  if (loadError && !form) return <ErrorState compact thing="your quote terms" error={loadError} onRetry={load} />;

  const clauseCount = String(form.quoteTerms || '').split('\n').filter((l) => l.trim()).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--cs-blue-dark, #1565C0)', background: 'var(--cw-blue-tint)', border: '1px solid var(--cs-blue-border, #bfdbfe)', borderRadius: 10, padding: '10px 13px', lineHeight: 1.5 }}>
        <i className="fa-solid fa-circle-info" aria-hidden="true" style={{ marginTop: 3 }} />
        <span>
          These print as the last page of every quote you send. Put <strong>one clause per line</strong>; each becomes a
          bullet. The validity and payment clauses are added automatically from the numbers below, so they always match the quote.
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12 }}>
        <Field label="Quotes are valid for (days)" error={daysError} hint="The default on a new quote. You can still change it on each quote.">
          <input type="number" inputMode="numeric" min="1" max="365" step="1"
            value={form.quoteValidDays ?? ''}
            onChange={(e) => set('quoteValidDays', e.target.value === '' ? '' : Number(e.target.value))} />
        </Field>
        <div>
          <p className="cw-label" style={{ margin: '0 0 5px' }}>Payment terms</p>
          <p className="cw-meta" style={{ margin: 0 }}>
            Set in <strong>Settings → Company</strong> and printed as
            “Payment term is strictly N days from date of invoice.”
          </p>
        </div>
      </div>

      <div style={{ ...box, padding: 12 }}>
        <Field label="Terms and conditions" hint={`${clauseCount} ${clauseCount === 1 ? 'clause' : 'clauses'}`}>
          <textarea style={{ minHeight: 200, lineHeight: 1.55 }} value={form.quoteTerms || ''} onChange={(e) => set('quoteTerms', e.target.value)}
            placeholder="One clause per line" />
        </Field>
      </div>

      <div style={{ ...box, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <h4 className="cw-label" style={{ margin: 0 }}>Quotation section presets</h4>
          <button type="button" className="cw-btn cw-btn-secondary"
            onClick={() => set('quoteSectionPresets', [...(form.quoteSectionPresets || []), { title: 'New section', body: '', bullets: [] }])}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> Add preset
          </button>
        </div>
        <p className="cw-meta" style={{ margin: '0 0 8px' }}>
          Offered as one-click headings when writing a quote. A <strong>table</strong> preset gives a costed
          breakdown inside the body instead of prose.
        </p>
        {(form.quoteSectionPresets || []).map((pr, i) => (
          <div key={i} style={{ border: '1px solid var(--cw-border)', borderRadius: 8, padding: 10, marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 8, alignItems: 'end' }}>
              <Field label="Heading" error={presetErrors[i]}>
                <input value={pr.title || ''} maxLength={120} onChange={(e) => setPreset(i, { title: e.target.value })} />
              </Field>
              <Field label="Type">
                <select value={pr.kind === 'table' ? 'table' : 'text'}
                  onChange={(e) => setPreset(i, e.target.value === 'table'
                    ? { kind: 'table', rows: pr.rows || [{ label: '', amount: '' }] }
                    : { kind: undefined, bullets: pr.bullets || [] })}>
                  <option value="text">Prose</option>
                  <option value="table">Table</option>
                </select>
              </Field>
              <div className="cw-field">
                <button type="button" className="cw-btn cw-btn-danger-outline" onClick={() => removePreset(i)}>
                  <i className="fa-solid fa-trash" aria-hidden="true" /> Remove
                </button>
              </div>
            </div>
            <Field label="Default wording" hint="Editable on each quote">
              <textarea style={{ minHeight: 60 }} value={pr.body || ''} onChange={(e) => setPreset(i, { body: e.target.value })} />
            </Field>
            {pr.kind !== 'table' && (
              <Field label="Default bullets" hint="One per line">
                <textarea id={`${uid}-b${i}`} style={{ minHeight: 50 }} value={(pr.bullets || []).join('\n')}
                  onChange={(e) => setPreset(i, { bullets: e.target.value.split('\n') })} />
              </Field>
            )}
          </div>
        ))}
      </div>

      <div style={{ ...box, padding: 12 }}>
        <Field label="Acceptance instructions" hint="Printed in a box at the end. Tell the client how to accept, for example by sending a PO quoting the reference.">
          <textarea style={{ minHeight: 110 }} value={form.quoteAcceptance || ''}
            onChange={(e) => set('quoteAcceptance', e.target.value)} />
        </Field>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {dirty && <span className="cw-meta" style={{ color: 'var(--cw-warning-fg)' }}>Unsaved changes</span>}
        {dirty && <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setForm(saved)} disabled={saving}>Discard</button>}
        <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving || !dirty}>
          <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} aria-hidden="true" /> {saving ? 'Saving…' : 'Save terms'}
        </button>
      </div>
    </div>
  );
};

export default QuoteTermsSettings;
